import { Component, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { currencyInfo, wasFormatted, type CurrencyInfo } from './currency'
import type { Flag } from './currencyFlags'

/**
 * HOVER A CURRENCY, ANYWHERE, AND A SLIM CARD NAMES IT.
 *
 * "VND" is obvious to the finance team in Hanoi and opaque to a reader in
 * Riyadh looking at the same group total. Hovering the code shows the issuer's
 * flag, the country, and the currency's full name.
 *
 * ---------------------------------------------------------------------------
 * ONE LAYER FOR THE WHOLE APP, NOT A COMPONENT PER FIGURE
 *
 * Mounted once (main.tsx). It listens on the document and works out what is
 * under the pointer, so no figure pays anything to be hoverable: the
 * transactions grid renders thousands of amounts, and a component per amount
 * would mean thousands of listeners and states for a card one reader opens now
 * and then.
 *
 * It also means money() and compact() keep returning STRINGS. Those same
 * strings feed the Excel and PDF extracts, native title tooltips and the
 * figure components' string props; turning them into elements would change what
 * an extract contains, and the rule is that an extract is exactly the screen.
 *
 * ---------------------------------------------------------------------------
 * HOW A CURRENCY IS RECOGNISED — TWO WAYS, BOTH NARROW
 *
 * 1. An element that says so: data-currency="SAR". Used where a code stands
 *    without a figure beside it — Material Planning's Currency column and the
 *    unit suffix after its amounts — because nothing about a bare "SAR" proves
 *    it is money. See currencyMark() below.
 *
 * 2. A text node whose WHOLE content is a figure in the exact shape the
 *    formatters produce: "VND 4,939,243,600", "+VND 1,018,668,674",
 *    "VND -4,020", "−SAR 3.09M", "$45.34B". The whole node, not a phrase inside
 *    one — a customer called "SAR TRADING 2" never matches — AND the code must
 *    be one money() or compact() has actually formatted in this session
 *    (currency.ts). Those two together are what stop "CUP 250" in a product
 *    column being called Tongan pa'anga.
 *
 * The code's own glyphs are the target, not the cell: the pointer must be over
 * the letters (or the "$", with a little slack because it is one narrow glyph),
 * which is where the reader's eye already is when they wonder what VND is.
 *
 * ---------------------------------------------------------------------------
 * WHAT IT DELIBERATELY DOES NOT DO
 *
 * - No card on touch. There is no hover, and a card that appears on tap would
 *   fight the tap that opens a row.
 * - No keyboard stop per amount. Every figure becoming a tab stop would wreck
 *   keyboard travel through a grid to reach a supplementary label; the code
 *   itself is already read out. The card is aria-hidden for the same reason.
 * - No animation. Motion is reserved for the sign-in surfaces (styles.css);
 *   the card simply appears after a short hover intent, and disappears at once.
 * - Nothing in print or in an extract.
 */

/* Leading space, sign, then either "ABC " or "$", then a number money() or
   compact() could have written (with money()'s own hyphen-minus after the unit
   for negatives), then compact()'s K/M/B. Anchored at both ends. */
const FIGURE = /^(\s*)([+\-−]?)(?:([A-Z]{3}) |(\$))[+\-−]?\d[\d,]*(?:\.\d+)?[KMB]?\s*$/

/* Long enough that sweeping the pointer across a grid does not strobe cards,
   short enough to feel like a reply to the hover. */
const INTENT_MS = 150

interface Hit {
  code: string
  rect: DOMRect
  /* What is being hovered, so a pointer moving within the same code does not
     restart the intent timer on every frame. */
  node: Node
  start: number
}

interface CaretDocument {
  caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null
  caretRangeFromPoint?: (x: number, y: number) => Range | null
}

/** The text node under a point: caretPositionFromPoint (standard), caretRangeFromPoint (older Chromium and Safari). */
function textNodeAt(x: number, y: number): Text | null {
  const d = document as unknown as CaretDocument
  const node =
    typeof d.caretPositionFromPoint === 'function'
      ? (d.caretPositionFromPoint(x, y)?.offsetNode ?? null)
      : typeof d.caretRangeFromPoint === 'function'
        ? (d.caretRangeFromPoint(x, y)?.startContainer ?? null)
        : null
  return node && node.nodeType === Node.TEXT_NODE ? (node as Text) : null
}

/* The caret APIs snap to the NEAREST caret position, even from empty cell
   padding or the far side of a row. The box test is what makes a hit mean the
   pointer is actually on the code. */
function within(rect: DOMRect, x: number, y: number, slackX: number): boolean {
  return x >= rect.left - slackX && x <= rect.right + slackX && y >= rect.top - 1 && y <= rect.bottom + 1
}

function findCurrency(target: EventTarget | null, x: number, y: number): Hit | null {
  const marked = target instanceof Element ? target.closest<HTMLElement>('[data-currency]') : null
  const markedCode = marked?.dataset.currency
  if (marked && markedCode) {
    const rect = marked.getBoundingClientRect()
    if (within(rect, x, y, 0)) return { code: markedCode, rect, node: marked, start: 0 }
  }

  const text = textNodeAt(x, y)
  if (!text) return null
  const m = FIGURE.exec(text.data)
  if (!m) return null

  const code = m[3] ?? 'USD'
  if (!wasFormatted(code)) return null

  const start = (m[1] ?? '').length + (m[2] ?? '').length
  const range = document.createRange()
  range.setStart(text, start)
  range.setEnd(text, start + (m[3] ? 3 : 1))
  const rect = range.getBoundingClientRect()
  return within(rect, x, y, m[3] ? 1 : 4) ? { code, rect, node: text, start } : null
}

/* The flags load the first time a currency is hovered, not with the app. By the
   time the intent delay has passed they are nearly always in; if not, the card
   shows a neutral box of the same size and the flag replaces it without the
   card moving. A failed load (a chunk gone after a deploy) settles on no flags
   rather than retrying on every hover. */
let flagsLoaded: Record<string, Flag> | null = null
let flagsLoading: Promise<Record<string, Flag>> | null = null

function loadFlags(): Promise<Record<string, Flag>> {
  flagsLoading ??= import('./currencyFlags')
    .then((m) => (flagsLoaded = m.FLAGS))
    .catch(() => (flagsLoaded = {}))
  return flagsLoading
}

/**
 * The flags, for anything that wants to draw one — the currency picker as well
 * as this card.
 *
 * Exported so there is ONE flag rendering rather than two that drift: the box
 * below is the same 18x12 with the same hairline, so a flag in the picker and
 * the same flag in the hover card are the same object at the same size. The
 * chunk still loads on demand and is shared between them.
 */
export function useFlags(): Record<string, Flag> | null {
  const [flags, setFlags] = useState(flagsLoaded)
  useEffect(() => {
    if (flags) return
    let live = true
    void loadFlags().then((f) => live && setFlags(f))
    return () => {
      live = false
    }
  }, [flags])
  return flags
}

/**
 * A flag in a fixed box, or the empty box of the same size.
 *
 * ALWAYS the box, even with no flag: a row whose flag is missing must occupy
 * the same width as one that has it, or a list of currencies ripples as the
 * chunk arrives.
 */
export function FlagBox({ region, flags }: { region: string | null; flags: Record<string, Flag> | null }) {
  const F = region && flags ? flags[region] : undefined
  return (
    <span className="relative inline-block h-3 w-[18px] shrink-0 overflow-hidden rounded-[2px] bg-(--color-surface-3)">
      {F && <F className="block h-full w-full" />}
      {/* A hairline over the edge, so a mostly white flag (Japan) still has an
          outline against a white row. */}
      <span className="absolute inset-0 rounded-[2px] border border-black/10" />
    </span>
  )
}

/**
 * A bare currency code, marked so the hover card can name it. For codes that
 * stand without a figure beside them; a formatted amount needs nothing.
 * Returns the input unchanged when it is empty, so a column that showed nothing
 * still shows nothing.
 */
export function currencyMark(code: string | null | undefined): ReactNode {
  return code ? <span data-currency={code}>{code}</span> : code
}

/** Mount once, at the root. */
export function CurrencyHoverLayer() {
  return (
    <Silent>
      <Layer />
    </Silent>
  )
}

/* A hover card must never be the reason a screen fails. The app's own
   ErrorBoundary variants both draw a failure screen; this one draws nothing. */
class Silent extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false }
  static getDerivedStateFromError() {
    return { failed: true }
  }
  render() {
    return this.state.failed ? null : this.props.children
  }
}

function Layer() {
  const [shown, setShown] = useState<{ info: CurrencyInfo; rect: DOMRect } | null>(null)
  const [flags, setFlags] = useState(flagsLoaded)

  useEffect(() => {
    let frame = 0
    let timer = 0
    let x = 0
    let y = 0
    let target: EventTarget | null = null
    let current: { node: Node; start: number } | null = null

    const clear = () => {
      window.clearTimeout(timer)
      timer = 0
      if (current) {
        current = null
        setShown(null)
      }
    }

    const evaluate = () => {
      frame = 0
      let hit: Hit | null = null
      try {
        hit = findCurrency(target, x, y)
      } catch {
        hit = null
      }
      if (!hit) return clear()
      if (current && current.node === hit.node && current.start === hit.start) return

      const info = currencyInfo(hit.code)
      if (!info.name) return clear()

      clear()
      current = { node: hit.node, start: hit.start }
      /* NO `if (!flagsLoaded)` GUARD. It was there, and it broke the flags on
         23 Sep 2026 the moment a SECOND consumer appeared.

         The guard tested the module-level cache, not this component's state.
         While the hover card was the only thing that loaded flags the two
         could not disagree; then the currency picker started calling useFlags()
         on every report page, so by the time anyone hovered, `flagsLoaded` was
         already populated — the guard was false, setFlags never ran, and this
         Layer's own state stayed at the null it captured when it mounted. The
         card drew an empty box for every currency, forever, with nothing in the
         console to say why.

         loadFlags() memoises its own import, so calling it on every hover costs
         one already-resolved promise and a microtask, and React discards a set
         to the identical object. Laziness is preserved — the chunk still does
         not load until something asks — and the state can no longer be stale,
         because nothing is now deciding on this component's behalf whether it
         still needs telling. */
      void loadFlags().then(setFlags)
      const rect = hit.rect
      timer = window.setTimeout(() => setShown({ info, rect }), INTENT_MS)
    }

    const onMove = (e: PointerEvent) => {
      if (e.pointerType === 'touch') return
      x = e.clientX
      y = e.clientY
      target = e.target
      if (!frame) frame = requestAnimationFrame(evaluate)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') clear()
    }

    /* Anything that moves the figure out from under a card that was placed
       against it hides the card; the next pointer move places a fresh one.
       Scroll is captured because the grids scroll inside their own containers,
       and a scroll event does not bubble. */
    document.addEventListener('pointermove', onMove, { passive: true })
    document.addEventListener('pointerdown', clear, true)
    document.addEventListener('scroll', clear, { capture: true, passive: true })
    document.addEventListener('keydown', onKey)
    document.documentElement.addEventListener('pointerleave', clear)
    window.addEventListener('resize', clear)
    window.addEventListener('blur', clear)
    return () => {
      cancelAnimationFrame(frame)
      window.clearTimeout(timer)
      document.removeEventListener('pointermove', onMove)
      document.removeEventListener('pointerdown', clear, true)
      document.removeEventListener('scroll', clear, true)
      document.removeEventListener('keydown', onKey)
      document.documentElement.removeEventListener('pointerleave', clear)
      window.removeEventListener('resize', clear)
      window.removeEventListener('blur', clear)
    }
  }, [])

  if (!shown) return null
  return createPortal(<CurrencyCard info={shown.info} anchor={shown.rect} flags={flags} />, document.body)
}

function CurrencyCard({ info, anchor, flags }: { info: CurrencyInfo; anchor: DOMRect; flags: Record<string, Flag> | null }) {
  const ref = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null)

  /* Measured before paint, so the card never flashes at the corner. Above the
     code by default — below it a card would cover the next row, the one the
     reader is about to move to — and below only when there is no room above.
     Centred on the code, then held inside the viewport. */
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const margin = 8
    const gap = 6
    const vw = document.documentElement.clientWidth
    const vh = document.documentElement.clientHeight
    const w = el.offsetWidth
    const h = el.offsetHeight
    let top = anchor.top - gap - h
    if (top < margin) top = Math.min(anchor.bottom + gap, vh - h - margin)
    const left = Math.max(margin, Math.min(anchor.left + anchor.width / 2 - w / 2, vw - w - margin))
    setPos({ left: Math.round(left), top: Math.round(top) })
  }, [anchor])

  const FlagSvg = info.region && flags ? flags[info.region] : undefined

  return (
    <div
      ref={ref}
      aria-hidden
      className="no-print pointer-events-none fixed left-0 top-0 z-[60] flex items-center gap-2 whitespace-nowrap rounded-md border border-(--color-line) bg-(--color-surface) py-1 pl-1.5 pr-2.5 text-(length:--text-small) leading-4 shadow-(--shadow-raised)"
      style={{
        transform: pos ? `translate(${pos.left}px, ${pos.top}px)` : undefined,
        visibility: pos ? 'visible' : 'hidden',
      }}
    >
      {info.region && (
        <span className="relative inline-block h-3 w-[18px] shrink-0 overflow-hidden rounded-[2px] bg-(--color-surface-3)">
          {FlagSvg && <FlagSvg className="block h-full w-full" />}
          {/* A hairline over the edge, so a mostly white flag (Japan) still has
              an outline against the white card. */}
          <span className="absolute inset-0 rounded-[2px] border border-black/10" />
        </span>
      )}
      {info.country && (
        <>
          <span className="font-semibold text-(--color-ink)">{info.country}</span>
          <span className="h-3 w-px bg-(--color-line-strong)" />
        </>
      )}
      <span className="text-(--color-ink-3)">{info.name}</span>
    </div>
  )
}
